import type { Ref, UnwrapNestedRefs } from 'vue';
import type { IMoviePlayerControlArea } from 'src/models';
import type { MoviePlayerState } from 'src/models/moviePlayer';

export interface PlaybackControlConfig {
  refControlArea: Ref<IMoviePlayerControlArea | undefined>;
  // 全video要素にchangeVjsPlayMode()を呼ぶ
  changeVideoPlayMode: (isPlayBackward: boolean) => void;
}

export interface PlaybackControlContext {
  playMovie: () => void;
  pauseMovie: () => void;
  playUI: () => void;
  pauseUI: () => void;
  togglePlayPauseUI: () => void;
  tickMovieBackward: () => Promise<void>;
  tickMovieForward: () => Promise<void>;
  setPlaySpeed: (val: number) => void;
  setVolume: (val: number) => void;
  playModeChange: (isPlayBackward: boolean) => void;
  emitSeekChange: (msec: number) => void;
  // 動画遷移側（setCurrentMovie）が再生再開要否を判定するために参照する現在のUI再生状態
  getUIPlayState: () => 'playing' | 'paused';
  // オーケストレーション層からのvideoControlProperties.playSpeed直接参照を避けるためのgetter
  getCurrentPlaySpeed: () => number;
}

// 再生制御（play/pause/tick/speed/volume/再生方向切替）とseek変化通知を担うcomposable。
// 他の分割composableに依存しない独立した実装。
export function useMoviePlayerPlaybackControl(
  state: UnwrapNestedRefs<MoviePlayerState>,
  config: PlaybackControlConfig,
): PlaybackControlContext {
  const playMovie = () => {
    state.playback.isPlaying = true;
    state.playback.videoControlProperties.play++;
  };
  const pauseMovie = () => {
    state.playback.isPlaying = false;
    state.playback.videoControlProperties.pause++;
  };
  const playUI = () => {
    playMovie();
  };
  const pauseUI = () => {
    pauseMovie();
  };
  const togglePlayPauseUI = () => {
    if (state.playback.isPlaying) {
      pauseUI();
    } else {
      playUI();
    }
  };
  const getUIPlayState = () => {
    return state.playback.isPlaying ? 'playing' : 'paused';
  };
  const tickMovieBackward = async() => {
    pauseUI();
    // 同一の動画内でのみコマ戻し
    state.playback.videoControlProperties.tickMsec -= 100; // 0.1秒
  };
  const tickMovieForward = async() => {
    pauseUI();
    // 同一の動画内でのみコマ送り
    state.playback.videoControlProperties.tickMsec += 100; // 0.1秒
  };
  const emitSeekChange = (msec: number) => {
    if (!config.refControlArea.value) return;

    config.refControlArea.value.emitSeekChange(msec);
  };
  const setPlaySpeed = (val: number) => {
    state.playback.videoControlProperties.playSpeed = val;
  };
  const getCurrentPlaySpeed = () => {
    return state.playback.videoControlProperties.playSpeed;
  };
  const setVolume = (val: number) => {
    state.playback.videoControlProperties.volume = val;
  };
  const playModeChange = (isPlayBackward: boolean) => {
    state.playback.videoControlProperties.isPlayBackward = isPlayBackward;
    config.changeVideoPlayMode(isPlayBackward);
  };

  return {
    playMovie,
    pauseMovie,
    playUI,
    pauseUI,
    togglePlayPauseUI,
    tickMovieBackward,
    tickMovieForward,
    setPlaySpeed,
    setVolume,
    playModeChange,
    emitSeekChange,
    getUIPlayState,
    getCurrentPlaySpeed,
  };
}
